import {
  createSlice,
  createAsyncThunk,
  createSelector,
} from "@reduxjs/toolkit";
import { restaurantTableService } from "../services/restaurantTable.service";

export const fetchTables = createAsyncThunk(
  "restaurantTable/fetchAll",
  async (_, thunkAPI) => {
    try {
      return await restaurantTableService.getTables();
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to fetch tables");
    }
  },
);

export const fetchTablesByRoomId = createAsyncThunk(
  "restaurantTable/fetchByRoomId",
  async (roomId, thunkAPI) => {
    try {
      const tables = await restaurantTableService.getTablesByRoomId(roomId);
      return { roomId, tables };
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to fetch tables for room");
    }
  },
);

export const fetchTableById = createAsyncThunk(
  "restaurantTable/fetchById",
  async (id, thunkAPI) => {
    try {
      return await restaurantTableService.getTableById(id);
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to fetch table");
    }
  },
);

export const addTable = createAsyncThunk(
  "restaurantTable/add",
  async (table, thunkAPI) => {
    try {
      return await restaurantTableService.createTable(table);
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to create table");
    }
  },
);

export const editTable = createAsyncThunk(
  "restaurantTable/edit",
  async ({ id, table }, thunkAPI) => {
    try {
      return await restaurantTableService.updateTable(id, table);
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to update table");
    }
  },
);

export const removeTable = createAsyncThunk(
  "restaurantTable/remove",
  async (id, thunkAPI) => {
    try {
      await restaurantTableService.hardDeleteTable(id);
      return id;
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to delete table");
    }
  },
);

export const occupyTable = createAsyncThunk(
  "restaurantTable/occupy",
  async ({ tableId, guestCount, statusId, shopId }, thunkAPI) => {
    try {
      await restaurantTableService.occupyTable(tableId, {
        guestCount,
        statusId,
        shopId,
      });
      return { tableId };
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to occupy table");
    }
  },
);

export const freeTable = createAsyncThunk(
  "restaurantTable/free",
  async ({ tableId, statusId, shopId }, thunkAPI) => {
    try {
      await restaurantTableService.freeTable(tableId, { statusId, shopId });
      return { tableId };
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to free table");
    }
  },
);

// REMOVED the premature export from here

const restaurantTableSlice = createSlice({
  name: "restaurantTable",
  initialState: {
    tables: [],
    tablesByRoomId: {},
    table: null,
    loading: false,
    occupancyLoading: false,
    error: null,
    showModal: false,
    showDeleteModal: false,
  },
  reducers: {
    openModal: (state) => {
      state.showModal = true;
    },
    closeModal: (state) => {
      state.showModal = false;
    },
    openDeleteModal: (state) => {
      state.showDeleteModal = true;
    },
    closeDeleteModal: (state) => {
      state.showDeleteModal = false;
    },
    updateTablePosition: (state, action) => {
      const { tableId, posX, posY } = action.payload;

      // 1. Update in the main tables array
      const table = state.tables.find((t) => t.restaurantTableId === tableId);
      if (table) {
        table.posX = posX;
        table.posY = posY;
      }

      // 2. Update in the tablesByRoomId map (THIS IS WHAT THE UI USES!)
      Object.keys(state.tablesByRoomId).forEach((roomId) => {
        if (state.tablesByRoomId[roomId]) {
          const roomTable = state.tablesByRoomId[roomId].find(
            (t) => t.restaurantTableId === tableId,
          );
          if (roomTable) {
            roomTable.posX = posX;
            roomTable.posY = posY;
          }
        }
      });
    },
    tableUpdatedFromSocket: (state, { payload }) => {
      const targetTableId = payload.tableId || payload.restaurantTableId;
      const newGuestCount =
        payload.guestCount !== undefined
          ? payload.guestCount
          : payload.currentGuestCount;
      const { statusId, occupiedSince } = payload;

      const patch = (table) =>
        table.restaurantTableId === targetTableId
          ? {
              ...table,
              statusId: statusId !== undefined ? statusId : table.statusId,
              currentGuestCount:
                newGuestCount !== undefined
                  ? newGuestCount
                  : table.currentGuestCount,
              occupiedSince:
                occupiedSince !== undefined
                  ? occupiedSince
                  : table.occupiedSince,
            }
          : table;

      state.tables = state.tables.map(patch);

      Object.keys(state.tablesByRoomId).forEach((roomId) => {
        if (state.tablesByRoomId[roomId]) {
          state.tablesByRoomId[roomId] =
            state.tablesByRoomId[roomId].map(patch);
        }
      });

      if (state.table && state.table.restaurantTableId === targetTableId) {
        state.table = patch(state.table);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTables.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTables.fulfilled, (state, action) => {
        state.loading = false;
        state.tables = action.payload;
      })
      .addCase(fetchTables.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchTablesByRoomId.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchTablesByRoomId.fulfilled, (state, action) => {
        const { roomId, tables } = action.payload;
        state.tablesByRoomId[roomId] = tables;
        state.loading = false;
      })
      .addCase(fetchTablesByRoomId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchTableById.fulfilled, (state, action) => {
        state.table = action.payload;
      })
      .addCase(addTable.pending, (state) => {
        state.loading = true;
      })
      .addCase(addTable.fulfilled, (state, action) => {
        state.tables.push(action.payload);
        state.loading = false;
        state.showModal = false;
      })
      .addCase(editTable.pending, (state) => {
        state.loading = true;
      })
        .addCase(editTable.fulfilled, (state, action) => {
        const updatedTable = action.payload; // The full table object from backend
        const tableId = action.meta.arg.id;
        
        // Update in main tables array
        const index = state.tables.findIndex(t => t.restaurantTableId === tableId);
        if (index !== -1) {
          state.tables[index] = { ...state.tables[index], ...updatedTable };
        }
        
        // Update in tablesByRoomId
        Object.keys(state.tablesByRoomId).forEach((roomId) => {
          if (state.tablesByRoomId[roomId]) {
            const roomIndex = state.tablesByRoomId[roomId].findIndex(
              t => t.restaurantTableId === tableId
            );
            if (roomIndex !== -1) {
              state.tablesByRoomId[roomId][roomIndex] = { 
                ...state.tablesByRoomId[roomId][roomIndex], 
                ...updatedTable 
              };
            }
          }
        });
        
        state.loading = false;
        state.showModal = false;
      })
      .addCase(removeTable.pending, (state) => {
        state.loading = true;
      })
      .addCase(removeTable.fulfilled, (state, action) => {
        const deletedTableId = action.meta.arg;
        state.tables = state.tables.filter(
          (t) => t.restaurantTableId !== deletedTableId,
        );
        state.loading = false;
        state.showDeleteModal = false;
      })
      .addCase(occupyTable.pending, (state) => {
        state.occupancyLoading = true;
      })
      .addCase(occupyTable.fulfilled, (state) => {
        state.occupancyLoading = false;
      })
      .addCase(occupyTable.rejected, (state, action) => {
        state.occupancyLoading = false;
        state.error = action.payload;
      })
      .addCase(freeTable.pending, (state) => {
        state.occupancyLoading = true;
      })
      .addCase(freeTable.fulfilled, (state) => {
        state.occupancyLoading = false;
      })
      .addCase(freeTable.rejected, (state, action) => {
        state.occupancyLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  openModal,
  closeModal,
  openDeleteModal,
  closeDeleteModal,
  tableUpdatedFromSocket,
  updateTablePosition,
} = restaurantTableSlice.actions;

// Selectors
export const selectTables = (state) => state.restaurantTable.tables;
export const selectTableById = (id) =>
  createSelector([selectTables], (tables) =>
    tables.find((t) => t.restaurantTableId === id),
  );

export const selectTablesForRoom = (roomId) =>
  createSelector(
    [(state) => state.restaurantTable.tablesByRoomId],
    (tablesByRoomId) => tablesByRoomId[roomId] || [],
  );

export default restaurantTableSlice.reducer;
